import {
  applySpec,
  assocPath,
  compose,
  defaultTo,
  dissocPath,
  filter,
  find,
  map,
  omit,
  path,
  pathEq,
  prop,
  propEq,
  propOr,
  when
} from 'ramda';
import { useEffect, useState } from 'react';
import { useProcess } from '@fasstech/oip-starter-utils/app';
import useSaveSouscriptionActivePolicyMutation from './useSaveSouscriptionActivePolicyMutation.js';
import useGetSouscriptionActivePolicySearchCompanyQuery from '@@views/souscriptionActivePolicy/client/hooks/useGetSouscriptionActivePolicySearchCompanyQuery.js';
import { isNotNil } from '@@serverHelpers';

const useActivePolicyForm = ({ middleFeatures, form, project, activePoliciesPath, basePath }) => {
  const [loading, setLoading] = useState(false);
  const clientId = prop('teamIdJrmc', project);
  const riskName = prop('offerCode', project);

  const { getSouscriptionActivePolicySearchCompany: jrmcCompaniesData } = useGetSouscriptionActivePolicySearchCompanyQuery({
    variables: {
      clientId,
      riskName
    },
    skip: !clientId || !riskName
  });

  const { processHandlers, context } = useProcess();

  const saveActivePolicy = useSaveSouscriptionActivePolicyMutation();

  const contractOnGoing = form.watch('activePolicy.contractOnGoing');
  const { isValid } = form.formState;

  const effectDate = prop('effectDate', project);

  useEffect(() => {
    form.trigger();
  }, []);

  const { IN_CHARGE_RESP, DONNEES_SOUSCRIPTION_JRMC_EST_NECESSAIRE } = middleFeatures;
  const inChargeOptions = compose(
    map(
      applySpec({
        label: prop('NOM'),
        value: prop('CODE'),
        needsCancellation: propEq('AFFICHAGE_CHAMPS', 'true'),
        fieldsAreRequired: propEq('CHAMPS_OBLIGATOIRES', 'true')
      })
    ),
    filter(propEq('AUTORISE', 'true'))
  )(IN_CHARGE_RESP || []);

  const selectedInChargeData = compose(
    defaultTo({}),
    find(propEq('value', form.watch(`${basePath}.cancellationApplicant`)))
  )(inChargeOptions);

  const jrmc = DONNEES_SOUSCRIPTION_JRMC_EST_NECESSAIRE === true && selectedInChargeData.value === 'FASST';
  const fieldsAreRequired = selectedInChargeData.fieldsAreRequired;

  useEffect(() => {
    form.clearErrors([
      `${basePath}.name`,
      `${basePath}.insuranceAddress.fields`,
      `${basePath}.insuranceAddress.postCode`,
      `${basePath}.insuranceAddress.city`
    ]);
  }, [fieldsAreRequired, basePath]);

  const onSubmit = (values) => {
    if (loading) return;
    setLoading(true);
    const getCompanyId = (companies, value) => {
      if (!companies || !value) {
        return null;
      }
      return prop('id', find(propEq('value', value), companies));
    };
    const externalId = getCompanyId(
      prop('jrmcCompanies', jrmcCompaniesData),
      path([...activePoliciesPath, 0, 'name'], values)
    );
    values = compose(
      omit(['activePolicy', 'effectDate', 'offerCode', 'teamIdJrmc']),
      when(
        pathEq(['activePolicy', 'contractOnGoing'], false),
        dissocPath(activePoliciesPath)
      ),
      when(
        () => isNotNil(externalId),
        assocPath([...activePoliciesPath, 0, 'externalId'], externalId)
      )
    )(values);

    saveActivePolicy.onFormSubmit(values, (ok, error) => {
      if (ok && !error) {
        processHandlers.onNext({
          context: {
            projectId: context?.projectId
          }
        });
      } else {
        setLoading(false);
      }
    });
  };

  const onPrevious = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId
      }
    });
  };

  return {
    contractOnGoing,
    inChargeOptions,
    selectedInChargeData,
    onSubmit,
    onPrevious,
    fieldsAreRequired,
    isValid,
    jrmcCompanies: propOr([], 'jrmcCompanies', jrmcCompaniesData),
    jrmc,
    effectDate
  };
};

export default useActivePolicyForm;
